import { getTenantFromUrl, mapApiError } from '@/api/axiosInstance'
import type { DataFilter, DataFilterCondition } from '@/api/dashboardsApi'
import { TIMEOUT_NONE } from '@/api/timeouts'
import { getTypeFromClickhouseToTS } from '@/utils/clickhouseTypes'
import { dateConditionValueToBackend } from '@/utils/dateUtils'
import axios, { type AxiosProgressEvent } from 'axios'
import { z } from 'zod'
import { validationPatterns } from '../config/constants'

// Column-type classification lives in `@/utils/clickhouseTypes` — the single source of truth. Re-exported
// here because these two names have consumers across the app that predate that module; prefer importing
// them (and the `is*FieldType` predicates, which have no re-export) from there in new code.
export { type ClickhouseToTSTypes, getTypeFromClickhouseToTS } from '@/utils/clickhouseTypes'

export function getKeyFromPath(path: string): string {
	const mainPath = path.split('?')[0].split('#')[0]

	// This would need to be imported from constants or passed as parameter
	const urlToDocId: Record<string, string> = {
		devices: 'device-management',
		dashboards: 'dashboards',
		actions: 'actions',
		firmwares: 'firmware',
		'device-configs': 'device-configs',
		streams: 'streams',
		users: 'users',
		roles: 'roles',
		'action-types': 'action-types',
		'metadata-keys': 'metadata-keys',
		settings: 'settings',
		alerts: 'alerts',
		'session-types': 'session-types',
		'session-rules': 'session-rules',
		'alert-rules': 'session-rules',
		'alert-groups': 'alert-groups',
		'dbc-parsers': 'dbc-parsers',
		'device-components': 'device-components',
	}

	for (const key in urlToDocId) {
		if (mainPath.includes(`/${key}`)) {
			return key
		}
	}

	return 'na'
}

export async function uploadFile(
	url: string,
	body: FormData,
	onUploadProgress: (p: AxiosProgressEvent) => void,
	signal?: AbortSignal,
): Promise<unknown> {
	const tenant = getTenantFromUrl()

	try {
		const res = await axios.request({
			method: 'POST',
			baseURL: '/api',
			url: encodeURI(url),
			data: body,
			onUploadProgress: p => onUploadProgress(p),
			headers: {
				'x-bytebeam-tenant': tenant,
			},
			withCredentials: true,
			timeout: TIMEOUT_NONE,
			signal,
		})

		return res
	} catch (error) {
		throw mapApiError(error)
	}
}

export type AtLeastOnePropRequired<T, K extends keyof T = keyof T> = {
	[Key in K]: Required<Pick<T, Key>> & Partial<Omit<T, Key>>
}[K]

/**
 * - regex: Case-sensitive regular expression matching
 * - iregex: Case-insensitive regular expression matching (i = insensitive)
 * - ansiregex: Case-insensitive regex matching after stripping ANSI escape codes (e.g., terminal color codes)
 */
export const REGEX_OPERATORS = new Set(['regex', 'iregex', 'ansiregex'])

export type SimpleConditionOperator =
	| '>'
	| '<'
	| '='
	| '!='
	| 'not='
	| '>='
	| '<='
	| 'in'
	| 'IS NULL'
	| 'IS NOT NULL'
	| 'regex'
	| 'iregex'
	| 'ansiregex'
export type CompositeConditionOperator = 'and' | 'or'
export type NoDateConditionOperator = 'no_data'

export type SimpleCondition = {
	field: string
	operator: SimpleConditionOperator
	value: unknown
}

export type CompositeCondition = {
	operator: CompositeConditionOperator
	conditions: Condition[]
}

export type NoDataCondition = {
	operator: NoDateConditionOperator
	threshold_seconds?: number
}

export type Condition = SimpleCondition | CompositeCondition | NoDataCondition

/**
 * Returns a filtered list of query operators based on the provided data type.
 *
 * @param {string} type - The data type for which to get the operators.
 *                        Supported types are "int", "float", "Date", "boolean", and "string".
 * @returns {Array} A filtered list of query operators suitable for the given data type.
 *
 * - For "int", "float", and "Date" types, it allows comparison and equality operators.
 * - For "boolean" type, it allows only equality operators and null checks.
 * - For "string" type, it allows only equality operators and null checks.
 * - For any other type, it returns all available query operators.
 */
export const getOperatorsOptionsByDataType = (
	queryOperators: {
		key: string
		value: string
		text: string
	}[],
	type: string,
	isNullable = false,
) => {
	let filteredOperators: {
		key: string
		value: string
		text: string
	}[] = []

	switch (type) {
		case 'int':
		case 'uint':
		case 'float':
			filteredOperators = queryOperators.filter(
				op => ['=', 'not=', '!=', '>', '<', '>=', '<=', 'in'].includes(op.value), // Allow comparison, equality, and IN for numeric types
			)
			break
		case 'Date':
			filteredOperators = queryOperators.filter(
				op => ['=', 'not=', '!=', '>', '<', '>=', '<='].includes(op.value), // Allow comparison and equality for date types (no IN)
			)
			break
		case 'string':
			filteredOperators = queryOperators.filter(
				op => ['=', 'not=', '!=', 'in'].includes(op.value), // Equality and IN operators for strings
			)
			break
		case 'boolean':
			filteredOperators = queryOperators.filter(
				op => ['=', 'not=', '!='].includes(op.value), // Only equality operators for booleans (no IN - backend doesn't support boolean arrays)
			)
			break
		default:
			filteredOperators = queryOperators // Default case, returns all operators
	}

	if (isNullable) {
		const nullOperators = queryOperators.filter(op => ['IS NULL', 'IS NOT NULL'].includes(op.value))

		filteredOperators = [...filteredOperators, ...nullOperators]
	}

	return filteredOperators
}

export type ActionsNavbarActiveItemType = 'Action Details' | 'Inventory' | 'New Action' | 'Action Settings'

export interface Auth {
	url: string
	type: string
}

export type Permission = {
	devices: { [key: string]: 'all' | string[] }
	tables: { [key: string]: 'all' | string[] }

	viewMetadata: 'all' | string[]
	editMetadata: 'all' | string[]

	dashboardPermittedShareRoles: 'all' | number[]

	createDashboards: boolean

	allowedActions: 'all' | string[]

	viewFiles: boolean
	editFiles: boolean

	viewFirmwares: boolean
	editFirmwares: boolean

	viewDeviceConfigs: boolean
	editDeviceConfigs: boolean

	viewStreams: boolean
	editStreams: boolean

	viewUsers: boolean
	editUsers: boolean

	viewRoles: boolean
	editRoles: boolean

	viewActionTypes: boolean
	editActionTypes: boolean

	showDeviceManagementTab: boolean
	showDashboardsTab: boolean
	showActionsTab: boolean

	viewMetadataKeys: boolean
	editMetadataKeys: boolean
	editTenantSettings: boolean

	allowCreatingDevices: boolean
	allowMarkActionAsCompleted: boolean
	reviewActions: boolean

	// Sessions and Notifications Permissions
	viewSessionRules: boolean
	editSessionRules: boolean
	viewNotifications: boolean
	editNotifications: boolean

	// DBC Parsers Permissions
	viewDBCParsers: boolean
	manageDBCParsers: boolean
	updateDBCParsing: boolean
	downloadDBCFiles: boolean
}

/**
 * Validates if a string contains only alphanumeric characters and underscores.
 *
 * @param {string} name - The string to be validated.
 * @returns {boolean} True if the string contains only alphanumeric characters and underscores, false otherwise.
 */

/**
 * Validates if a string is a valid SQL identifier (ClickHouse compatible).
 * Rules:
 * - Must start with an ASCII alphabetic character (a-z or A-Z).
 * - Subsequent characters can be alphanumeric or underscores (_).
 * - Length between 1 and 63 characters.
 */
export const validateSqlIdentifier = (name: string): { isValid: boolean; message?: string } => {
	if (!name) {
		return { isValid: false, message: 'Name is required' }
	}

	if (name.length > 63) {
		return { isValid: false, message: 'Name must be between 1 and 63 characters long' }
	}

	if (!/^[a-zA-Z]/.test(name)) {
		return { isValid: false, message: 'Name must start with an ASCII alphabetic character (a-z or A-Z)' }
	}

	if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
		return { isValid: false, message: 'Name can only contain alphanumeric characters and underscores' }
	}

	return { isValid: true }
}

/**
 * Reusable Zod schema for SQL identifiers (streams, fields, etc.)
 * @param fieldName - The human-readable name of the field for error messages
 */
export const sqlIdentifierSchema = (fieldName: string) =>
	z
		.string()
		.min(1, `${fieldName} is required!`)
		.superRefine((val, ctx) => {
			const result = validateSqlIdentifier(val)
			if (!result.isValid) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: result.message?.replace('Name', fieldName) || `${fieldName} is invalid`,
				})
			}
		})

/**
 * Reusable Zod schema for normalized identifiers (lowercase letters, numbers, and underscores).
 */
export const normalizedIdentifierSchema = (fieldName: string) =>
	z
		.string()
		.min(1, `${fieldName} is required!`)
		.regex(/^[a-z0-9_]+$/, `${fieldName} can only contain lowercase letters, numbers, and underscores`)

/**
 * Reusable Zod schema for normalized SQL identifiers (e.g. ClickHouse table names).
 * Composition: first validates as a normalized identifier (lowercase, numbers, underscores),
 * then applies SQL identifier rules from sqlIdentifierSchema.
 */
export const normalizedSqlIdentifierSchema = (fieldName: string) =>
	normalizedIdentifierSchema(fieldName).pipe(sqlIdentifierSchema(fieldName))

/**
 * Validates if a string contains a valid URL.
 * This function uses the URL constructor to validate the URL, with additional checks for the hostname and domainPattern.
 * @param {string} url - The URL to be validated.
 * @returns {boolean} True if the URL is valid, false otherwise.
 */
export const isURLValid = (url: string): boolean => {
	try {
		new URL(url)
		return true
	} catch {
		return false
	}
}

export function convertToURLParams(values: object) {
	const e = Object.entries(values).map(([key, values]) => [key, Array.isArray(values) ? values.join(',') : values])
	return new URLSearchParams(Object.fromEntries(e))
}

export function dropDownOptionsFromArray(array: string[]) {
	let options = [
		{
			key: 'no-options',
			value: 'no-options',
			text: 'No options available',
		},
	]
	if (array !== undefined && array.length > 0) {
		options = array.map(s => {
			return {
				key: s,
				value: s,
				text: s,
			}
		})
	}
	return options
}

// Operator definitions for condition builder
const allQueryOperators = [
	{ key: 'equals', text: '=', value: '=' },
	{ key: 'notEquals', text: '!=', value: 'not=' },
	{ key: 'greaterThan', text: '>', value: '>' },
	{ key: 'lessThan', text: '<', value: '<' },
	{ key: 'greaterThanOrEqual', text: '>=', value: '>=' },
	{ key: 'lessThanOrEqual', text: '<=', value: '<=' },
	{ key: 'isNull', text: 'IS NULL', value: 'IS NULL' },
	{ key: 'isNotNull', text: 'IS NOT NULL', value: 'IS NOT NULL' },
]

// Operator definitions for session rules (sessions backend expects '!=' not 'not=')
const sessionQueryOperators = [
	{ key: 'equals', text: '=', value: '=' },
	{ key: 'notEquals', text: '!=', value: '!=' },
	{ key: 'greaterThan', text: '>', value: '>' },
	{ key: 'lessThan', text: '<', value: '<' },
	{ key: 'greaterThanOrEqual', text: '>=', value: '>=' },
	{ key: 'lessThanOrEqual', text: '<=', value: '<=' },
	{ key: 'in', text: 'IN', value: 'in' },
	{ key: 'isNull', text: 'IS NULL', value: 'IS NULL' },
	{ key: 'isNotNull', text: 'IS NOT NULL', value: 'IS NOT NULL' },
]

// Get operators for field type
export const getOperatorsForField = (clickhouseType: string) => {
	const type = getTypeFromClickhouseToTS(clickhouseType)
	const isNullable = clickhouseType.includes('Nullable')

	return getOperatorsOptionsByDataType(allQueryOperators, type, isNullable)
}

// Get operators for a metadata field, which is untyped and so offers the same three everywhere
export const getOperatorsForMetadataField = () => [
	{ key: 'is', text: 'IS', value: 'is' },
	{ key: 'is_null', text: 'IS NULL', value: 'is_null' },
	{ key: 'is_not_null', text: 'IS NOT NULL', value: 'is_not_null' },
]

// Get operators for session rules
export const getOperatorsForSessionField = (clickhouseType: string) => {
	const type = getTypeFromClickhouseToTS(clickhouseType)
	const isNullable = clickhouseType.includes('Nullable')

	return getOperatorsOptionsByDataType(sessionQueryOperators, type, isNullable)
}

// Boolean options for boolean fields
export const getBooleanOptions = () => [
	{ key: 'true', text: 'true', value: true },
	{ key: 'false', text: 'false', value: false },
]

// Conjunctions for AND/OR
export const conjunctions = [
	{ key: 'and', text: 'AND', value: 'and' },
	{ key: 'or', text: 'OR', value: 'or' },
]

// Default condition
export const defaultCondition: CompositeCondition = {
	conditions: [{ field: '', operator: '=', value: '' }],
	operator: 'and',
}

// Simple helper functions
export const isCompositeCondition = (condition: Condition): condition is CompositeCondition => {
	return ['and', 'or'].includes(condition.operator)
}

export const toCompositeCondition = (condition: Condition): CompositeCondition => {
	return isCompositeCondition(condition) ? condition : { conditions: [condition], operator: 'and' }
}

export const getCondition = (conditions: Condition[], operator: string): Condition => {
	if (conditions.length === 1) {
		return conditions[0]
	}
	return { conditions, operator } as CompositeCondition
}

/**
 * Converts Condition object to HoneySQL query array format for API
 * @param condition - The Condition object from QueryBuilder
 * @returns Query array in format: ["and", ["=", {"column": "field"}, {"value": "val"}], ...]
 * @example
 * Input: { operator: 'and', conditions: [{ field: 'id', operator: '=', value: '123' }] }
 * Output: ["and", ["=", {"column": "id"}, {"value": "123"}]]
 */
export const conditionToQueryArray = (condition: Condition): unknown[] | undefined => {
	if (!condition) {
		return undefined
	}

	const compositeOperators = ['and', 'or']

	// Handle composite conditions (AND/OR)
	if (compositeOperators.includes(condition.operator)) {
		const compositeCondition = condition as CompositeCondition
		const conditions = compositeCondition.conditions

		// Filter out empty conditions
		const validConditions = conditions.filter(cond => {
			if ('field' in cond) {
				const simpleCond = cond as SimpleCondition
				return simpleCond.field && simpleCond.field !== '' && simpleCond.value !== ''
			}
			return false
		})

		if (validConditions.length === 0) {
			return undefined
		}

		// Build query array: [operator, [op, {column: field}, {value: val}], ...]
		const queryArray: unknown[] = [compositeCondition.operator]

		for (const cond of validConditions) {
			const simpleCond = cond as SimpleCondition
			queryArray.push([simpleCond.operator, { column: simpleCond.field }, { value: simpleCond.value }])
		}

		return queryArray
	}

	// Handle simple conditions
	if ('field' in condition) {
		const simpleCond = condition as SimpleCondition

		// Skip empty conditions
		if (!simpleCond.field || simpleCond.field === '' || simpleCond.value === '') {
			return undefined
		}

		// Return single condition wrapped in array
		const result = [simpleCond.operator, { column: simpleCond.field }, { value: simpleCond.value }]
		return result
	}

	return undefined
}

/**
 * Converts HoneySQL query array to Condition object
 * For loading saved queries back into the QueryBuilder
 * @param query - Query array in format: ["and", ["=", {"column": "field"}, {"value": "val"}], ...]
 * @returns Condition object for QueryBuilder
 */
export const queryArrayToCondition = (query: unknown): Condition => {
	if (!query || !Array.isArray(query) || query.length === 0) {
		return defaultCondition
	}

	// Check if first element is a composite operator
	const compositeOperators = ['and', 'or']
	const firstElement = query[0] as string

	if (compositeOperators.includes(firstElement) && query.length > 1) {
		// Composite condition
		const operator = firstElement as 'and' | 'or'
		const conditions: SimpleCondition[] = []

		// Parse each condition (skip first element which is the operator)
		for (let i = 1; i < query.length; i++) {
			const item = query[i]
			if (Array.isArray(item) && item.length === 3) {
				const op = item[0] as SimpleConditionOperator
				const columnObj = item[1] as { column: string }
				const valueObj = item[2] as { value: unknown }

				conditions.push({
					field: columnObj.column,
					operator: op,
					value: valueObj.value,
				})
			}
		}

		if (conditions.length === 0) {
			return defaultCondition
		}

		return { conditions, operator } as CompositeCondition
	}

	// Simple condition (single element or just operator + column + value)
	if (query.length === 3) {
		const op = query[0] as SimpleConditionOperator
		const columnObj = query[1] as { column: string }
		const valueObj = query[2] as { value: unknown }

		return {
			field: columnObj.column,
			operator: op,
			value: valueObj.value,
		}
	}

	return defaultCondition
}

// DataFilter Conversion Utilities (for new v3 panel APIs)

/**
 * Maps SimpleCondition operator to DataFilterCondition operator key
 */
const operatorToDataFilterKey: Record<string, keyof Omit<DataFilterCondition, 'column'>> = {
	'=': 'eq',
	'not=': 'ne',
	'!=': 'ne',
	'>': 'gt',
	'>=': 'gte',
	'<': 'lt',
	'<=': 'lte',
	in: 'in',
	regex: 'regex',
	iregex: 'iregex',
	ansiregex: 'ansiregex',
}

/**
 * Converts a Condition object (from QueryBuilder) to DataFilter format (for v3 panel APIs)
 *
 * @param condition - The Condition object from QueryBuilder
 * @returns DataFilter object for API request, or undefined if condition is empty/invalid
 *
 * @example
 * Input: { operator: 'and', conditions: [{ field: 'region', operator: '=', value: 'north' }] }
 * Output: { and: [{ condition: { column: 'region', eq: 'north' } }] }
 */
export const conditionToDataFilter = (
	condition: Condition,
	columnTypes?: Record<string, string>,
): DataFilter | undefined => {
	if (!condition) {
		return undefined
	}

	// Handle composite conditions (AND/OR)
	if (isCompositeCondition(condition)) {
		const validFilters: DataFilter[] = []

		for (const cond of condition.conditions) {
			const converted = conditionToDataFilter(cond, columnTypes)
			if (converted) {
				validFilters.push(converted)
			}
		}

		if (validFilters.length === 0) {
			return undefined
		}

		// If only one filter, return it directly without wrapper
		if (validFilters.length === 1) {
			return validFilters[0]
		}

		// Return composite filter
		return condition.operator === 'and' ? { and: validFilters } : { or: validFilters }
	}

	// Handle NoDataCondition - not applicable for data filters
	if ('threshold_seconds' in condition || condition.operator === 'no_data') {
		return undefined
	}

	// Handle simple conditions
	const simpleCond = condition as SimpleCondition

	// Skip empty conditions
	if (!simpleCond.field || simpleCond.field === '') {
		return undefined
	}

	// Handle is/is not null operators.
	// QueryBuilder stores these as operator='='/'!=' with value=null (required by sessions rules backend).
	// Handle both that encoding and explicit 'IS NULL'/'IS NOT NULL' operator strings.
	if (simpleCond.operator === 'IS NULL' || (simpleCond.operator === '=' && simpleCond.value === null)) {
		return {
			condition: {
				column: simpleCond.field,
				isnull: true,
			},
		}
	}
	if (simpleCond.operator === 'IS NOT NULL' || (simpleCond.operator === '!=' && simpleCond.value === null)) {
		return {
			condition: {
				column: simpleCond.field,
				isnull: false,
			},
		}
	}

	// Skip if value is empty
	if (simpleCond.value === '' || simpleCond.value === undefined || simpleCond.value === null) {
		return undefined
	}

	// Map operator to DataFilter key
	const filterKey = operatorToDataFilterKey[simpleCond.operator]
	if (!filterKey) {
		console.warn(`Unsupported operator for DataFilter: ${simpleCond.operator}`)
		return undefined
	}

	// Build the condition
	const dataFilterCondition: DataFilterCondition = {
		column: simpleCond.field,
	}

	// Coerce a string to number/bool only when the column's type accepts it (int/float → number,
	// bool → bool); string/date columns keep the raw string. Regex ops always keep the string.
	// Falls back to shape-based coercion when the column type is unknown.
	const isRegexOperator = REGEX_OPERATORS.has(simpleCond.operator)
	const columnType = columnTypes?.[simpleCond.field]?.toLowerCase()
	const allowNumeric = columnType === undefined || columnType.includes('int') || columnType.includes('float')
	const allowBool = columnType === undefined || columnType.includes('bool')
	// Date/DateTime columns store epoch seconds in the condition (set by the QueryBuilder picker).
	// The platform backend expects an RFC3339 timestamp (DateTime) or `YYYY-MM-DD` (Date), so
	// convert here. Falls back to the raw value if it can't be resolved to a date.
	const isDateTimeColumn = columnType?.includes('datetime') ?? false
	const isDateColumn = (columnType?.includes('date') ?? false) && !isDateTimeColumn
	const parseValue = (val: unknown): unknown => {
		if (isDateTimeColumn || isDateColumn) {
			return dateConditionValueToBackend(val, isDateColumn) ?? val
		}
		if (isRegexOperator || typeof val !== 'string') {
			return val
		}
		const trimmed = val.trim()
		if (allowNumeric) {
			if (/^-?\d+$/.test(trimmed)) {
				return Number.parseInt(trimmed, 10)
			}
			if (/^-?\d+\.\d+$/.test(trimmed)) {
				return Number.parseFloat(trimmed)
			}
		}
		if (allowBool) {
			if (trimmed === 'true') {
				return true
			}
			if (trimmed === 'false') {
				return false
			}
		}
		return val
	}

	// Handle 'in' operator specially (expects array)
	if (filterKey === 'in') {
		const valueArray = Array.isArray(simpleCond.value)
			? simpleCond.value.map(parseValue)
			: String(simpleCond.value)
					.split(',')
					.map(v => parseValue(v.trim()))
		dataFilterCondition.in = valueArray.filter(
			(v): v is string | number => typeof v === 'string' || typeof v === 'number',
		)
	} else {
		// Assign parsed value to the appropriate key
		;(dataFilterCondition as unknown as Record<string, unknown>)[filterKey] = parseValue(simpleCond.value)
	}

	return { condition: dataFilterCondition }
}

/**
 * Converts a DataFilter object (from API/saved config) to Condition format (for QueryBuilder)
 *
 * @param filter - DataFilter object from API or saved dashboard config
 * @returns Condition object for QueryBuilder
 *
 * @example
 * Input: { and: [{ condition: { column: 'region', eq: 'north' } }] }
 * Output: { operator: 'and', conditions: [{ field: 'region', operator: '=', value: 'north' }] }
 */
export const dataFilterToCondition = (filter: DataFilter): Condition => {
	if (!filter) {
		return defaultCondition
	}

	// Handle AND composite
	if (filter.and && filter.and.length > 0) {
		const conditions = filter.and.map(f => dataFilterToCondition(f))
		return {
			operator: 'and',
			conditions,
		} as CompositeCondition
	}

	// Handle OR composite
	if (filter.or && filter.or.length > 0) {
		const conditions = filter.or.map(f => dataFilterToCondition(f))
		return {
			operator: 'or',
			conditions,
		} as CompositeCondition
	}

	// Handle NOT - wrap in a composite with negation note
	// Note: QueryBuilder doesn't have NOT UI, so we convert to simple condition
	if (filter.not) {
		const innerCondition = dataFilterToCondition(filter.not)
		return innerCondition
	}

	// Handle simple condition
	if (filter.condition) {
		const cond = filter.condition
		let operator: SimpleConditionOperator = '='
		let value: unknown = ''

		if (cond.eq !== undefined) {
			operator = '='
			value = cond.eq
		} else if (cond.ne !== undefined) {
			operator = 'not='
			value = cond.ne
		} else if (cond.gt !== undefined) {
			operator = '>'
			value = cond.gt
		} else if (cond.gte !== undefined) {
			operator = '>='
			value = cond.gte
		} else if (cond.lt !== undefined) {
			operator = '<'
			value = cond.lt
		} else if (cond.lte !== undefined) {
			operator = '<='
			value = cond.lte
		} else if (cond.in !== undefined) {
			operator = 'in'
			value = cond.in
		} else if (cond.regex !== undefined) {
			operator = 'regex'
			value = cond.regex
		} else if (cond.iregex !== undefined) {
			operator = 'iregex'
			value = cond.iregex
		} else if (cond.ansiregex !== undefined) {
			operator = 'ansiregex'
			value = cond.ansiregex
		} else if (cond.isnull !== undefined) {
			operator = cond.isnull ? 'IS NULL' : 'IS NOT NULL'
			value = null
		}

		return {
			field: cond.column,
			operator,
			value,
		} as SimpleCondition
	}

	return defaultCondition
}

export const TIME_RANGE_OPTIONS = [
	{ value: 'custom', label: 'Custom' },
	{ value: '1min', label: '1 minute' },
	{ value: '5min', label: '5 minutes' },
	{ value: '10min', label: '10 minutes' },
	{ value: '30min', label: '30 minutes' },
	{ value: '1hour', label: '1 hour' },
	{ value: '6hours', label: '6 hours' },
	{ value: '12hours', label: '12 hours' },
	{ value: '24hours', label: '24 hours' },
	{ value: '7days', label: '7 days' },
	{ value: '30days', label: '30 days' },
]

export const convertTimeRangeToMs = (rangeValue: string): string => {
	const oneMinInMs = 60 * 1000
	const oneHrInMs = 60 * oneMinInMs
	const oneDayInMs = 24 * oneHrInMs

	if (rangeValue.endsWith('ms')) {
		const msValue = rangeValue.replace('ms', '')
		return msValue // Return just the number without "ms"
	}

	switch (rangeValue) {
		case '1min':
			return String(1 * oneMinInMs)
		case '5min':
			return String(5 * oneMinInMs)
		case '10min':
			return String(10 * oneMinInMs)
		case '30min':
			return String(30 * oneMinInMs)
		case '1hour':
			return String(1 * oneHrInMs)
		case '6hours':
			return String(6 * oneHrInMs)
		case '12hours':
			return String(12 * oneHrInMs)
		case '24hours':
			return String(24 * oneHrInMs)
		case '7days':
			return String(7 * oneDayInMs)
		case '30days':
			return String(30 * oneDayInMs)
		default:
			// If it's already in "123ms" format or custom, return as-is
			return rangeValue
	}
}

export const convertMsToReadable = (msValue: string): string => {
	// If it's already a predefined option, return the label
	const timeOption = TIME_RANGE_OPTIONS.find(opt => opt.value === msValue)
	if (timeOption) {
		return timeOption.label
	}

	// Handle custom millisecond format
	if (!msValue.endsWith('ms')) {
		return msValue
	}

	const ms = Number.parseInt(msValue.replace('ms', ''), 10)
	const oneMinInMs = 60 * 1000
	const oneHrInMs = 60 * oneMinInMs
	const oneDayInMs = 24 * oneHrInMs

	if (ms >= oneDayInMs && ms % oneDayInMs === 0) {
		const days = Math.floor(ms / oneDayInMs)
		return `${days} day${days > 1 ? 's' : ''}`
	}
	if (ms >= oneHrInMs && ms % oneHrInMs === 0) {
		const hours = Math.floor(ms / oneHrInMs)
		return `${hours} hour${hours > 1 ? 's' : ''}`
	}
	if (ms >= oneMinInMs && ms % oneMinInMs === 0) {
		const minutes = Math.floor(ms / oneMinInMs)
		return `${minutes} minute${minutes > 1 ? 's' : ''}`
	}

	// For non-exact values, show the closest unit
	if (ms >= oneDayInMs) {
		const days = Math.floor(ms / oneDayInMs)
		return `~${days} day${days > 1 ? 's' : ''}`
	}
	if (ms >= oneHrInMs) {
		const hours = Math.floor(ms / oneHrInMs)
		return `~${hours} hour${hours > 1 ? 's' : ''}`
	}
	if (ms >= oneMinInMs) {
		const minutes = Math.floor(ms / oneMinInMs)
		return `~${minutes} minute${minutes > 1 ? 's' : ''}`
	}

	return `${ms}ms`
}

export const isWithinTimeRange = (timestamp: string | number | Date, rangeValue: string): boolean => {
	const now = new Date()

	// Convert string of pure digits (Unix millisecond timestamp) to a number so Date parses it correctly
	let parsedTimestamp = timestamp
	if (typeof timestamp === 'string' && /^\d+$/.test(timestamp)) {
		parsedTimestamp = Number.parseInt(timestamp, 10)
	}

	const timeValue = new Date(parsedTimestamp)

	if (Number.isNaN(timeValue.getTime())) {
		return false
	}

	const diffMs = now.getTime() - timeValue.getTime()
	const diffMinutes = diffMs / (1000 * 60)

	// ✅ Handle custom millisecond format like "2592000000ms"
	if (rangeValue.endsWith('ms')) {
		const customMs = Number.parseInt(rangeValue.replace('ms', ''), 10)
		return diffMs <= customMs
	}

	switch (rangeValue) {
		case '1min':
			return diffMinutes <= 1
		case '5min':
			return diffMinutes <= 5
		case '10min':
			return diffMinutes <= 10
		case '30min':
			return diffMinutes <= 30
		case '1hour':
			return diffMinutes <= 60
		case '6hours':
			return diffMinutes <= 360
		case '12hours':
			return diffMinutes <= 720
		case '24hours':
			return diffMinutes <= 1440
		case '7days':
			return diffMinutes <= 10080
		case '30days':
			return diffMinutes <= 43200
		default:
			return false
	}
}

export type ValidationResult = {
	isValid: boolean
	errorMessage: string | null
}

const validationStrategies: Record<string, (val: string) => ValidationResult> = {
	int: (val: string) => ({
		isValid: validationPatterns.integer.test(val),
		errorMessage: validationPatterns.integer.test(val) ? null : 'Please enter a valid integer.',
	}),
	uint: (val: string) => ({
		isValid: validationPatterns.wholeNumber.test(val),
		errorMessage: validationPatterns.wholeNumber.test(val) ? null : 'Please enter a valid whole number (0 or greater).',
	}),
	float: (val: string) => ({
		isValid: validationPatterns.float.test(val),
		errorMessage: validationPatterns.float.test(val) ? null : 'Please enter a valid decimal number.',
	}),
	boolean: (val: string) => ({
		isValid: val === 'true' || val === 'false',
		errorMessage: val === 'true' || val === 'false' ? null : 'Please enter a valid boolean (true/false).',
	}),
}

/**
 * Validates a user input value based on the column type.
 */
export const validateInputValue = (value: string, clickhouseType: string): string | null => {
	if (!value) {
		return null
	}
	const tsType = getTypeFromClickhouseToTS(clickhouseType)
	const strategy = validationStrategies[tsType]
	if (strategy) {
		return strategy(value).errorMessage
	}
	return null
}
